import { Box, Typography, useMediaQuery } from '@mui/material';
import { useEffect, type ReactElement, type ReactNode } from 'react';

import { EnvironmentBadge } from '@/ui/shell/environment';
import { fontWeights } from '@/ui/tokens';

import { BrandPill } from './BrandPill';
import { IllustrationPanel } from './IllustrationPanel';

/**
 * Mise en page commune aux trois ecrans sans coque : connexion, mot de passe
 * oublie, nouveau mot de passe (variante « Q » de la maquette P01).
 *
 * Deux zones : le panneau illustre a gauche, la colonne de 400 px a droite,
 * centree. **Aucune phrase ne dit a quoi sert l'outil** (exigence du
 * responsable produit) : le titre de l'ecran et l'instruction de saisie sont les
 * seuls textes, et le pied ne porte que le copyright.
 *
 * **Sous 900 px le panneau n'est pas rendu** : il ne tiendrait qu'au prix de la
 * colonne de formulaire, et ne pas le monter evite aussi de telecharger
 * l'illustration sur un telephone. La marque passe alors en tete de colonne.
 *
 * Le fond est le jeton `background` du socle, un peu plus fonce que le `#FAF7F8`
 * de la maquette (ecart a confirmer a la revue visuelle).
 */

/** Largeur de la colonne de formulaire (maquette). */
const COLUMN_WIDTH = 400;

/** Point de rupture du panneau : `md` du theme. */
const PANEL_MIN_WIDTH_QUERY = '(min-width: 900px)';

/** Titre de l'onglet du navigateur, restitue au demontage. */
const useDocumentTitle = (title: string): void => {
  useEffect(() => {
    const previous = document.title;

    document.title = `${title} — MiaMiam`;

    return () => {
      document.title = previous;
    };
  }, [title]);
};

export interface AuthLayoutProps {
  /** Titre visible de l'ecran (le `h1`). */
  readonly title: string;
  /** Titre de l'onglet du navigateur : le nom de l'ecran, sans le nom de marque. */
  readonly documentTitle: string;
  /** Une seule phrase d'instruction de saisie, jamais une description de l'outil. */
  readonly subtitle: string;
  readonly children: ReactNode;
}

export const AuthLayout = ({ title, documentTitle, subtitle, children }: AuthLayoutProps): ReactElement => {
  const withPanel = useMediaQuery(PANEL_MIN_WIDTH_QUERY, { noSsr: true });

  useDocumentTitle(documentTitle);

  return (
    <Box
      component="main"
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        bgcolor: 'background.default',
        color: 'text.primary',
        p: withPanel ? '24px' : 0,
      }}
    >
      {withPanel ? <IllustrationPanel /> : null}

      {/* Ou l'on se connecte, avant de se connecter : a la place du selecteur de langue. */}
      <Box sx={{ position: 'absolute', top: withPanel ? 40 : 16, right: withPanel ? 40 : 16 }}>
        <EnvironmentBadge />
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, md: 1 },
          pt: withPanel ? 0 : 2,
        }}
      >
        {withPanel ? null : (
          <Box sx={{ alignSelf: 'flex-start' }}>
            <BrandPill />
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
            maxWidth: COLUMN_WIDTH,
            py: 4,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 28, sm: 36 },
              lineHeight: { xs: '34px', sm: '42px' },
              fontWeight: fontWeights.extraBold,
              letterSpacing: '-0.02em',
              color: 'tokens.textStrong',
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ mt: 1, mb: 4.5, fontSize: 15, fontWeight: fontWeights.medium, color: 'tokens.textMuted' }}>
            {subtitle}
          </Typography>
          {children}
        </Box>

        <Typography
          component="footer"
          sx={{ width: '100%', maxWidth: COLUMN_WIDTH, fontSize: 13, fontWeight: fontWeights.semiBold, color: 'tokens.textMuted' }}
        >
          © {new Date().getFullYear()} MiaMiam
        </Typography>
      </Box>
    </Box>
  );
};
